import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { C } from "../lib/colors.js";
import { PieIcon, LightbulbIcon, AlertIcon } from "./Icons.jsx";
import { TIER_ORDER, TIER_SHORT, tagOwner, effectiveTier } from "../lib/forecast/engines.js";
import TierSelector from "./TierSelector.jsx";
import { isReadOnlyRow } from "../lib/forecast/sharedRowGuards.js";
import { reportClampHeight, isReportTruncated } from "../lib/chatReportClamp.js";
import { classifyChatChange, decideChatScroll } from "../lib/chatScroll.js";
import { beginIntent, ui } from "../lib/telemetry/instrument.js";
import { Button, IconButton, Spinner } from "./ui/Primitives.jsx";
import { IconAccount, IconArrowRight, IconArrowUp, IconClose, IconVolume } from "./ui/Icons.jsx";
import { CancelForecastControl } from "./RunStatus.jsx";
import { currentSectionEmpty, formatDividerTime } from "../lib/chat/chatJournal.js";

// Message type: 13px body on a 1.5 line height, no tracking (the shared .chat-message__body
// class sets 12px / 1.6, so the inline style wins).
const BODY = { fontSize: 13, lineHeight: 1.5, letterSpacing: 0 };

/**
 * The cap is measured from the live console rather than expressed in lines: the console is
 * resizable and font sizes differ by theme, so a line count means different things on
 * different setups. A ResizeObserver on the console and on the content re-evaluates on a
 * drag-resize or a late-arriving reflow instead of leaving a stale cut.
 *
 * When the body does not fit, the overflow is hidden and an ellipsis row is added BELOW the
 * clamp — a real element rather than a text-overflow, because `text-overflow: ellipsis`
 * only works on a single line. The caller renders the full-report link after this, so the
 * way out is always adjacent to the cut.
 */
function ClampedReportBody({ consoleRef, children }) {
  const bodyRef = React.useRef(null);
  const [cap, setCap] = React.useState(null);
  const [truncated, setTruncated] = React.useState(false);

  const measure = React.useCallback(() => {
    const nextCap = reportClampHeight(consoleRef?.current?.clientHeight);
    setCap(nextCap);
    const el = bodyRef.current;
    // scrollHeight is the UNCLAMPED height even while max-height is applied, which is
    // what makes this measurable without first rendering the text at full size.
    setTruncated(el ? isReportTruncated(el.scrollHeight, nextCap) : false);
  }, [consoleRef]);

  // Two effects on purpose. The observer wiring watches two STABLE nodes (the console and
  // this body div), so it is set up once; re-running it whenever `children` changed —
  // which is a fresh element on nearly every chat render — tore down and rebuilt an
  // observer, and each rebuild forced another layout read. Remeasuring on new content is
  // the separate, cheap half.
  React.useEffect(() => {
    measure();
  }, [measure, children]);

  React.useEffect(() => {
    if (typeof ResizeObserver !== "function") return undefined;
    const ro = new ResizeObserver(measure);
    if (consoleRef?.current) ro.observe(consoleRef.current);
    if (bodyRef.current) ro.observe(bodyRef.current);
    return () => ro.disconnect();
  }, [measure, consoleRef]);

  return (
    <>
      <div ref={bodyRef} style={cap != null ? { maxHeight: cap, overflow: "hidden" } : undefined}>
        {children}
      </div>
      {truncated && (
        <div aria-hidden="true" style={{ userSelect: "none", lineHeight: 1, marginTop: 2 }}>
          …
        </div>
      )}
    </>
  );
}

function Linkify({ text }) {
  if (!text) return null;
  const urlRx = /(https?:\/\/[^\s<>"')\]]+)/g;
  const parts = text.split(urlRx);
  return parts.map((p, i) => {
    if (p.match(/^https?:\/\//))
      return (
        <a
          {...ui("chat.message-link")}
          data-telemetry-redact=""
          key={i}
          href={p}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: C.purple, textDecoration: "underline", wordBreak: "break-all" }}
        >
          {p}
        </a>
      );
    return <span key={i}>{p}</span>;
  });
}
function TourText({ text, animate, onReveal }) {
  const tokens = useMemo(() => {
    const out = [];
    String(text || "")
      .split(/\*\*/)
      .forEach((seg, si) => {
        const bold = si % 2 === 1;
        seg.split(/(\s+)/).forEach((p) => {
          if (p.length) out.push({ text: p, bold, ws: /^\s+$/.test(p) });
        });
      });
    return out;
  }, [text]);
  const wordCount = tokens.reduce((n, t) => n + (t.ws ? 0 : 1), 0);
  const [shown, setShown] = useState(animate ? 0 : wordCount);
  useEffect(() => {
    // This branch also completes an in-progress reveal the instant a newer message
    // supersedes it.
    if (!animate || wordCount === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: snap the reveal to full immediately when this isn't the latest message (or it's empty/superseded); the interval animation below is the effect's real purpose.
      setShown(wordCount);
      return;
    }
    setShown(0);
    let n = 0;
    const id = setInterval(() => {
      n += 1;
      setShown(n);
      if (n >= wordCount) clearInterval(id);
    }, 45);
    return () => clearInterval(id);
  }, [animate, wordCount]);
  // Keep the chat pinned to the bottom as the reveal grows the message — without
  // this the message gets taller after the on-append auto-scroll already fired,
  // pushing its last line below the fold (clipped by a few px).
  useEffect(() => {
    onReveal && onReveal();
  }, [shown, onReveal]);
  let seen = 0;
  const parts = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    // Whitespace is withheld until a following word is revealed, so words don't briefly
    // run together and no trailing space is left.
    if (t.ws) {
      if (seen > 0 && seen < shown) parts.push(t);
      continue;
    }
    if (seen >= shown) break;
    seen += 1;
    parts.push(t);
  }
  return parts.map((t, i) =>
    t.bold ? <strong key={i}>{t.text}</strong> : <span key={i}>{t.text}</span>,
  );
}

/**
 * `chat.voice_input`: dictation into the composer started or stopped. Returns the handle: a
 * stop ends at once, a start when recognition starts (`ok`) or fails to (`error`).
 */
function beginVoiceInput(on) {
  const intent = beginIntent(
    "chat.voice_input",
    { on },
    {
      origin: "chat.voice-input",
      surface: "chat",
    },
  );
  if (!on) intent.end("ok");
  return intent;
}

/** A speech error that is a stop (the user's, or a newer utterance's), not a failure. */
const SPEECH_CANCELS = new Set(["canceled", "interrupted", "aborted"]);

const EXAMPLE_QS = [
  "Where does the Fed funds rate sit at the end of 2027?",
  "Will the EU enforce its AI Act on US firms by Q3 2027?",
  "Which range does US inflation land in by December 2027?",
  "Where does the Fed funds rate sit after the December 2026 meeting?",
  "Does the Fed cut rates at its December 2026 meeting?",
  "What range does the S&P 500 close 2027 in?",
  "Will the US enter a recession by Thanksgiving?",
  "What price range is Bitcoin in by November?",
  "Does a big US bank go under by 2027?",
  "Which sector leads S&P 500 returns in 2027?",
  "Will the WHO declare a new pandemic before Halloween 2027?",
  "What P/E multiple does Walmart (WMT) carry on July 31, 2027 if US comparable sales growth slows to 3% while EPS stays at current consensus?",
  "What range does US CPI inflation print in for December 2026?",
  "Will US CPI come in below 3% for December 2026?",
  "What range does US unemployment fall in by Q4 2027?",
  "Will Russia and Ukraine reach a ceasefire by winter 2027?",
  "Which party controls the US Senate after the 2028 election?",
  "Will Apple release an AI-native device by spring 2027?",
  "Which company ships the most AI accelerators in 2027?",
  "Will the S&P 500 reach 7,000 by Easter 2027?",
  "Which team wins Super Bowl LXI on February 7, 2027?",
  "Will the S&P 500 close above 7,000 during December 2026?",
  "Which currency gains the most against the dollar in 2027?",
  "Are we seeing lab-grown meat in US grocery stores by July 2027?",
  "Where does the 10-year Treasury yield sit on June 30, 2027 if the Fed delivers two cuts and core PCE holds near 2.5%?",
  "How many US states legalize sports betting by Q4 2028?",
  "Will the US national debt exceed $40T by Q3 2027?",
  "Which team wins the 2027 World Series?",
  "Does a deepfake influence a major election in 2028?",
  "What range does the S&P 500 close December 2026 in?",
  "Does Bitcoin close above $150K on January 31, 2027?",
  "What range does the 10-year Treasury yield close 2027 in?",
  "Can a private company land humans on the Moon by Q4 2028?",
  "Which country hosts the most data-center capacity by 2029?",
  "Will commercial supersonic flights resume by 2029?",
  "How many named Atlantic hurricanes form before Halloween?",
  "Will Netflix subscriber count decline year-over-year by April 2027?",
  "What range does Nvidia's forward P/E occupy on April 30, 2027 if data-center revenue growth halves while gross margin holds above 70%?",
  "How many US jobs does the November 2026 report add?",
  "Will US holiday retail sales grow more than 4% in December 2026?",
  "Which automaker leads US EV sales in 2028?",
  "Will the global population reach 8.5 billion by July 4th 2028?",
  "What share of new US cars are electric by July 2028?",
  "Will Japan raise interest rates above 1% by Q2 2027?",
  "Which AI lab holds the top-ranked model by Q3 2028?",
  "Does Starlink reach 10M subscribers by November?",
  "Which retailer leads US holiday sales in December 2026?",
  "Does a US government shutdown begin before January 2027?",
  "Which continent adds the most solar capacity in 2028?",
  "Will a Fortune 500 company collapse due to AI-related fraud by 2028?",
  "How far above the 10-year Treasury does the S&P 500 earnings yield sit on September 30, 2027 if index EPS grows 8% and yields stay flat?",
  "How many major central banks cut rates by Q2 2027?",
  "Will the Suez Canal face a multi-week closure again before St. Patrick's Day 2028?",
  "Which company has the largest market cap at the end of 2027?",
  "Does Anthropic's valuation exceed $100B by Q4 2027?",
  "What range does Bitcoin trade in at the end of January 2027?",
  "Will TSA screen a record number of travellers in December 2026?",
  "Which metal posts the biggest price move in 2027?",
  "Will global tourism revenue surpass $12 trillion by December 2028?",
  "Who wins the 2028 US presidential election?",
  "Will Costco's membership count exceed 150M by Q3 2028?",
  "Which country wins the most golds at the 2028 Olympics?",
  "Will a major pharmaceutical company lose patent exclusivity causing a 30%+ revenue drop by Black Friday 2027?",
  "Where does Brent crude trade on May 31, 2027 if OPEC+ holds current quotas while global demand grows 1%?",
  "Which film leads Oscar nominations in January 2027?",
  "Does OPEC+ cut production at its December 2026 meeting?",
  "Which industry sheds the most US jobs by Q4 2027?",
  "Will a major port strike disrupt US supply chains by the end of Q4?",
  "Which chipmaker leads foundry revenue by 2028?",
  "Does Walmart overtake Amazon in US e-commerce market share by 2029?",
  "How many US banks fail by the end of 2027?",
  "Will a major sovereign debt crisis emerge in Europe by May 2028?",
  "What range does the 10-year Treasury yield close December 2026 in?",
  "Will US Q4 2026 GDP growth exceed 2% when reported in January 2027?",
  "Which social platform gains the most US users by Q2 2027?",
  "Will the global semiconductor market exceed $1 trillion by 2028?",
  "What range does USD/JPY occupy on August 31, 2027 if the Bank of Japan hikes once and the Fed cuts twice?",
  "What range do US home prices move in through 2027?",
  "Does a Central Bank Digital Currency launch in a G7 country by April 2028?",
  "Which airline carries the most US passengers in 2027?",
  "Will private equity dry powder exceed $5 trillion globally by Q1 2028?",
  "Which team wins the College Football Playoff in January 2027?",
  "Will a major spot-crypto ETF post net outflows in January 2027?",
  "Which drugmaker leads GLP-1 revenue by Q3 2027?",
  "Will US data center energy consumption exceed 10% of total grid by September 2028?",
  "Where does the euro trade against the dollar by Q2 2027?",
  "Will a US state legalize prediction markets for political events before the midterm elections?",
  "Which country produces the most rare earths outside China by 2029?",
  "Will Airbnb face a regulatory ban in a major European capital by 2027?",
  "What dividend yield does Exxon (XOM) offer on June 30, 2027 if Brent averages $70 and the payout ratio is unchanged?",
  "What range does US unemployment print in for December 2026?",
  "Does Nvidia beat revenue estimates in its February 18, 2027 earnings?",
  "Which US state adds the most population by 2028?",
  "Will South Korea's birth rate rise above 1.0 by Q4 2028?",
  "What range does global food-price inflation land in for 2028?",
  "Will a major tech company spin off its cloud division by 2028?",
  "Which console leads holiday sales in 2027?",
  "Will the FIFA World Cup 2030 set a new total tournament attendance record?",
  "Which company announces the largest layoffs in January 2027?",
  "Will US flu hospitalisations peak before February 1, 2027?",
  "Which studio has the top-grossing release of 2028?",
  "Does an open-source LLM match GPT-5 performance on MMLU by Q3 2028?",
  "Where does US shelter inflation sit in the June 2027 CPI print if new-lease rents decelerate to 2% while wages grow 4%?",
  "Where does US healthcare spending sit as a share of GDP by 2028?",
  "Will US national park visitation exceed 330M annually by 2027?",
  "Which crypto holds the second-largest market cap by 2027?",
  "Will global video game industry revenue exceed $250B by 2027?",
  "Where does Brent crude trade on February 1, 2027?",
  "Will gold close above $4,000/oz on February 1, 2027?",
  "Which country leads global semiconductor capex in 2028?",
  "Will average US sticker college tuition rise year-over-year through 2027?",
  "What range does the rupee trade in against the dollar by 2027?",
  "Will the US craft brewery count remain above 9,000 through 2027?",
  "Which port handles the most US container volume in 2027?",
  "Will a Disney+ original series win an Emmy for Outstanding Drama before fall 2027?",
  "What range does the US high-yield credit spread occupy on July 31, 2027 if default rates stay under 3% and Treasury yields are unchanged?",
  "How many US states pass a 4-day workweek law by Memorial Day 2028?",
  "Will the US adult obesity rate decline year-over-year by 2027?",
  "Which league signs the largest streaming deal by 2027?",
  "Will a Grammy Album of the Year go to a country artist by spring 2028?",
  "What share of US electricity comes from solar by Q4 2027?",
  "Will a major US sports league sign a billion-dollar streaming-rights deal by 2027?",
  "Which retailer leads US e-commerce share by 2029?",
  "Will at least one US state pass a 4-day workweek law by Memorial Day 2028?",
  "How many GLP-1 drugs win FDA approval by Q3 2027?",
  "Will the average MLB game length stay under 2 hours 40 minutes through the 2027 World Series?",
  "Which European capital restricts short-term rentals hardest by 2027?",
  "Will US passport renewal wait times drop below 4 weeks by Q3 2027?",
  "How many rate cuts does the Fed deliver by September 30, 2027 if unemployment reaches 5% while core inflation holds at 3%?",
  "Where does US defense spending land by Q2 2028?",
  "Will a new GLP-1 weight-loss drug receive FDA approval by Q3 2027?",
  "Which artist wins Grammy Album of the Year in spring 2028?",
  "Are people spending over $400B on pets globally by Q4 2028?",
  "How many major US sports franchises relocate by 2029?",
  "Will the Smithsonian's annual visitation surpass 30M by 2027?",
  "Which economy grows fastest in Southeast Asia by November 2028?",
  "Will average global airfares stay above 2019 levels through 2027?",
  "How many US states legalize prediction markets before the midterms?",
  "Where do US corn futures trade on July 31, 2027 if Midwest yields come in 5% below trend?",
  "Will the BLS average price for a dozen eggs top $2.50 in April 2027?",
  "Does the USDA's January 2027 WASDE raise its 2026/27 US soybean export forecast?",
  "Does cocoa trade above $6,000/ton on March 31, 2027?",
  "How many US farms file for Chapter 12 bankruptcy in 2027?",
  "Does the USDA's February 2027 forecast put 2027 net farm income below 2026's?",
  "Does the BLS average retail price of ground beef set a new record in the November data?",
  "Will Chipotle open more than 370 new restaurants in 2027?",
  "Does Chick-fil-A's US systemwide sales top $25B in Technomic's 2027 Top 500 report?",
  "What range does US hotel RevPAR land in for July 2027, per STR?",
  "Does Marriott's global room count top 1.95M by Q4 2027?",
  "Will US restaurant menu prices rise more than 3% year-over-year in the June 2027 CPI?",
  "Will Las Vegas visitor volume grow year-over-year in November, per the LVCVA's monthly report?",
  "What range do US housing starts print in for March 2027?",
  "Will CBRE's US office vacancy rate fall below 17.5% by Q4 2027?",
  "Where do 30-year mortgage rates sit on June 30, 2027 if the Fed cuts twice and the 10-year holds near 4%?",
  "Which Case-Shiller metro shows the largest year-over-year price drop in the June 2027 report?",
  "Will lumber futures close above $575 per thousand board feet on December 31?",
  "Does a top-10 US homebuilder announce a merger by Q3 2027?",
  "Will global insured catastrophe losses exceed $100B in 2027?",
  "Does Florida's Citizens Property Insurance policy count fall below 200,000 by June 30, 2027?",
  "What range does Florida's average homeowners premium land in for 2027?",
  "Does Progressive report a combined ratio above 92 for Q2 2027?",
  "Does Florida approve 5+ new property insurers to enter the market before hurricane season opens on June 1?",
  "Where does the Drewry World Container Index sit the week before Christmas?",
  "Will UPS report year-over-year growth in US average daily package volume in its Q2 2027 earnings?",
  "Does FedEx report year-over-year US ground volume growth in its December earnings?",
  "Do US Class 8 truck orders exceed 300,000 units in 2027?",
  "Will Amazon Logistics handle more than 8 billion US parcels in 2027, per the Pitney Bowes Parcel Shipping Index?",
  "Does Aurora operate more than 750 driverless trucks commercially by December 31, 2027?",
  "Will Boeing deliver more than 60 commercial jets in December?",
  "Which US airline posts the best on-time rate for summer 2027 (June–August), per DOT?",
  "Does California High-Speed Rail begin laying track before March 31, 2027?",
  "Will the FAA certify an eVTOL air taxi for passenger service by Q4 2027?",
  "How many SpaceX Starship launches happen by year-end?",
  "Will Amtrak ridership top 38 million trips for the fiscal year ending September 30, 2027?",
  "Do Toyota and Lexus combined sell more than 10.8 million vehicles globally in 2027?",
  "Will Tesla run a paid robotaxi service in 20+ US metro areas by Q4 2027?",
  "What range does the average US new-car transaction price land in for December 2027?",
  "Does a Chinese automaker begin selling cars in the US by December 31, 2028?",
  "Will Waymo complete more than 1M paid rides per week by June 30, 2027?",
  "Where does US auto-loan delinquency sit in Q2 2027 if unemployment rises to 5%?",
  "Does the NRC issue a construction permit or combined license for a new large (1 GW+) US reactor by June 30, 2028?",
  "Where does Henry Hub natural gas trade on New Year's Eve?",
  "Does a US utility sign a new single data-center power contract of 10 GW+ by June 30, 2027?",
  "Do US electricity prices rise more than 5% year-over-year in the December 2027 CPI?",
  "Will a small modular reactor deliver power to the US grid by 2030?",
  "What range does US grid battery-storage capacity reach by the end of 2027, per EIA?",
  "Where does copper trade on June 30, 2027 if China's GDP growth slows to 4%?",
  "Will battery-grade lithium carbonate trade above $30,000/t at any point in Q4 2027, per Benchmark?",
  "Which company produces the most copper in 2027?",
  "Does US raw steel output rise year-over-year in H1 2027, per AISI?",
  "Will silver close above $70/oz on March 31, 2027?",
  "Does NOAA issue The Metals Company a commercial deep-sea mining permit by June 30, 2027?",
  "Will median US hospital operating margins top 3% in Kaufman Hall's December 2027 report?",
  "How many rural US hospitals close in 2027, per the UNC Sheps Center?",
  "Does HCA Healthcare report more than $82B in revenue for 2027?",
  "Does the US uninsured rate rise above 9% in the CDC's Q2 2027 estimate?",
  "Will Teladoc's revenue grow year-over-year in Q2 2027?",
  "Do ACA open-enrollment signups trail last year's in CMS's mid-December snapshot?",
  "Does the FDA approve an oral Alzheimer's drug by June 30, 2028?",
  "How many novel drugs does the FDA's CDER approve in 2027?",
  "Which pharma company leads global revenue in 2027?",
  "Does the FDA approve Intellia's CRISPR therapy lonvo-z for hereditary angioedema by its March 10, 2027 PDUFA date?",
  "Will US biotech IPOs raise more than $5B in H1 2027?",
  "Is a medtech acquisition valued at $20B+ announced by September 30, 2027?",
  "Will US college enrollment grow year-over-year in the National Student Clearinghouse's fall 2027 preliminary report?",
  "How many nonprofit US colleges announce a closure or merger in 2027?",
  "What range does the average SAT score land in for the class of 2027?",
  "How many US states have universal ESA or voucher programs, per EdChoice, by July 1, 2027?",
  "Does new international student enrollment fall in IIE's fall 2027 Open Doors snapshot?",
  "Is another single gift of $500M+ to a US university announced before New Year's?",
  "Do gross proceeds in the FCC's upper C-band spectrum auction exceed $50B?",
  "Which US wireless carrier adds the most postpaid phone subscribers in Q2 2027?",
  "Does the HHS breach portal list a single healthcare breach affecting 10M+ people reported by June 30, 2027?",
  "What range does global cybersecurity spending land in for 2027, per Gartner?",
  "Will fiber broadband pass more than 75% of US households by the end of 2027, per the Fiber Broadband Association?",
  "Does the FBI's IC3 report for 2026, due around April 2027, show more than $25B in losses?",
  "Does Tesla sell Optimus robots to consumers by December 31, 2028?",
  "Does Tesla report producing 10,000+ Optimus robots by December 31, 2027?",
  "Does Google or IBM announce a system with more than 100 logical qubits by December 31, 2028?",
  "Will industrial robot installations top 650,000 in IFR's World Robotics report in September 2027?",
  "Does Serve Robotics operate more than 3,000 delivery robots by Q4 2027?",
  "Will Neuralink implant more than 100 human patients by Q4 2027?",
  "Will LVMH report year-over-year revenue growth in its H1 2027 results?",
  "Will Macy's operate fewer than 400 namesake stores at fiscal year-end on January 29, 2028?",
  "What range does NRF forecast for back-to-school spending in its July 2027 survey?",
  "Will US online sales this holiday season (Nov–Dec) exceed $275B, per Adobe?",
  "Does ThredUp's spring 2028 Resale Report put the US secondhand apparel market above $65B?",
  "Will Nike's revenue grow in the fiscal year ending May 31, 2027?",
  "Will US prestige beauty sales grow more than 5% in Circana's H1 2027 report?",
  "Does US beer volume decline more than 3% year-over-year in 2027, per Beer Institute shipment data?",
  "Will non-alcoholic beer reach 5% of US beer sales by the end of 2028, per NielsenIQ?",
  "Does Coca-Cola report global unit-case volume growth of 3% or more in Q2 2027?",
  "What range does Procter & Gamble's organic sales growth land in for the fiscal year ending June 30, 2027?",
  "Does US spirits revenue decline year-over-year in DISCUS's annual report for 2027?",
  "Will US digital ad revenue exceed $370B in 2027, per IAB?",
  "Which streaming service has the most US subscribers at the end of 2027?",
  "Does USA TODAY Co. (formerly Gannett) or Lee Enterprises end daily print at 10+ papers by June 30, 2027?",
  "Does Super Bowl LXI draw more than 130M US viewers?",
  "Will US podcast ad revenue exceed $3.6B in 2027, per IAB?",
  "Will YouTube ad revenue exceed $14.5B in Q4 2027?",
  "Which concert tour grosses the most worldwide in 2027, per Billboard?",
  "Does Spotify raise its US Premium individual price above $12.99 by June 30, 2027?",
  "Does global box office exceed $37B in 2027, per Gower Street Analytics?",
  "Will vinyl revenue grow more than 10% year-over-year in the RIAA's mid-year 2027 report?",
  "Which title wins Game of the Year at The Game Awards in December?",
  "Will Universal's Epic Universe draw more than 10M visitors in 2027, per the TEA/AECOM Theme Index?",
  "Does any NBA franchise other than the Lakers sell at a valuation above $8B by June 30, 2028?",
  "Does the NFL play a 2027 regular-season game in a city that has never hosted one?",
  "What range does the average NFL team valuation land in for Forbes' 2027 NFL valuations list?",
  "Will the LA 2028 Olympics sell more than 10M Olympic tickets (excluding Paralympics) by the closing ceremony?",
  "Does the average WNBA franchise valuation top $550M in Sportico's 2027 rankings?",
  "Which F1 team wins the 2027 Constructors' Championship?",
  "Will total stablecoin market cap exceed $400B by Q4 2027?",
  "Is a US bank merger valued above $15B announced by June 30, 2027?",
  "Does Stripe go public by the end of 2027?",
  "What range does US credit-card delinquency land in for Q2 2027?",
  "Does US Buy Now, Pay Later spending on Cyber Monday top $1.08B, per Adobe?",
  "Will Coinbase report more than $375B in assets under custody for Q2 2027?",
  "Does the US union membership rate for 2027 rise year-over-year when BLS reports it in January 2028?",
  "Will US average hourly earnings growth exceed 3% year-over-year in the June 2027 jobs report?",
  "How many major US work stoppages (1,000+ workers) happen in 2027, per BLS?",
  "Does Kastle's 10-city weekly office occupancy average top 58% in any week by June 30, 2027?",
  "Is the federal minimum wage raised by the end of 2028?",
  "What range does the US labor-force participation rate land in for December 2027, per BLS?",
  "Does the Supreme Court agree to hear an AI training fair-use copyright case by June 30, 2028?",
  "How many US states have comprehensive privacy laws by the end of 2028, per IAPP?",
  "Does Congress pass a law preempting state AI regulation by the end of 2027?",
  "What range does the average US effective tariff rate land in on June 30, 2027, per the Yale Budget Lab?",
  "Which Big Tech company pays the largest antitrust fine in 2027?",
  "Does the USPS raise the Forever stamp above $0.84 by July 31, 2027?",
  "Will 2027 be the hottest year on record, per NOAA?",
  "How many tornadoes does NOAA's Storm Prediction Center confirm by June 30, 2027?",
  "Does Lake Mead fall below 1,000 feet by the end of the water year on September 30, 2027?",
  "Where do EU carbon allowance prices close on December 31, 2027?",
  "Will Arctic sea ice hit a record low in September 2027?",
  "How many acres burn in US wildfires by September 30, 2027, per NIFC?",
  "Will US gym memberships exceed 88M in 2027, per HFA?",
  "Does Oura report more than 10M rings sold by June 30, 2027?",
  "Does US senior-housing occupancy exceed 92% by Q4 2027, per NIC MAP?",
  "Does the CPI day care and preschool index rise more than 4% year-over-year in the November print?",
  "Does the CPI veterinarian services index rise more than 6% year-over-year in the June 2027 print?",
  "Does PepsiCo report a year-over-year volume decline for Frito-Lay North America in Q2 2027?",
];
// Stable per-item React key: identity-based, not array index. Index keys
// remount bubbles when items reorder (rehydration/adoption inserts older
// messages), resetting TourText reveal + mismapping speakingIdx.
// `_jkey` is the chat journal's key (lib/chat/chatJournal.js): stable across forecasts and
// from first sight to recorded, so a line keeps its bubble when it lands in the history.
const itemKey = (it) =>
  it._jkey
    ? it._jkey
    : it.kind === "event"
      ? `event:${it.runId}`
      : it.kind === "narration"
        ? `narration:${it.runId}:${it.ordinal}`
        : `entry:${it.ts}:${it.role}:${it.entryKind || ""}:${it.content}`;
// Filled with the fixed brand lavender rather than inheriting the bubble's text colour:
// the palette is otherwise colourless, so the mark is one of the few things that keeps one.
function SoothMark() {
  return (
    <div
      title="Sayer"
      style={{
        width: 10,
        height: 10,
        display: "block",
        background: "#D783FF",
        WebkitMaskImage: "url(/logo.svg)",
        maskImage: "url(/logo.svg)",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  );
}
function ReadAloudButton({ text, itemKey, speakingIdx, onSpeak }) {
  const isSpeaking = speakingIdx === itemKey;
  return (
    <IconButton
      {...ui("chat.read-aloud")}
      label={isSpeaking ? "Stop reading" : "Read aloud"}
      aria-pressed={isSpeaking}
      onClick={() => onSpeak(text, itemKey)}
      style={{ width: 24, height: 24, color: isSpeaking ? C.accent : undefined }}
    >
      <IconVolume style={{ width: 14, height: 14 }} />
    </IconButton>
  );
}
export default function ChatContent({
  theme: _theme,
  items,
  input,
  setInput,
  onSubmit,
  onReset,
  loading,
  chatThinking,
  forecast,
  forecastEngine,
  setForecastEngine: _setForecastEngine,
  healthError,
  onRetryHealth,
  orchestrators,
  onSelectTier,
  quickDeepEnabled,
  quickDeepSelectorEnabled,
  voiceMode,
  listening,
  setListening,
  speaking,
  setSpeaking,
  recognitionRef,
  proColors,
  activeTrace,
  canCancel,
  onCancelForecast,
  askHeroShowing = false,
  runStatusShowing = false,
  onSkipTour,
  tourActive,
  tourWaitKey,
  onTourNext,
  tourExamples,
  enterFlash,
  endTourBtn,
  onEndTourBtn,
  postTourActive,
  onEndPostTour,
  onOpenReport,
  // Continuous chat: opens the forecast a history divider names; whether the panel is open.
  onOpenForecast,
  panelOpen,
}) {
  // `readOnly` gates only the OWNER-ONLY affordances in the empty state below, not the
  // composer. `forecast` here is `active` (App.jsx's own prop name for it), and
  // `useChatIntent` refuses the three branch-minting intents for recipients.
  const readOnly = isReadOnlyRow(forecast);
  const endRef = useRef(null);
  const scrollRef = useRef(null);
  // The composer BOX (frame + textarea + control bar). The tier menu renders into it so
  // it lines up with that frame by construction — see TierSelector's `overlayRef`.
  const composerBoxRef = useRef(null);
  const latestMsgRef = useRef(null);
  const prevNewestKeyRef = useRef(null);
  // The summary is followed by a short run-projected "Prediction: N%" line, so it is not
  // the newest item when the scroll effect runs; it is tracked separately to pin its top.
  const summaryMsgRef = useRef(null);
  const prevSummaryKeyRef = useRef(null);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  // column-reverse: scrollTop === 0 is the visual BOTTOM (newest). endRef is the
  // first child (the bottom sentinel), so this pins the view to the newest content.
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    endRef.current?.scrollIntoView({ block: "end" });
    if (el) el.scrollTop = 0;
  }, []);
  // The panel opens on the newest lines, older history above. scrollTop only, no scrollIntoView:
  // the panel is still off-screen when this runs, and scrollIntoView would move its ancestors.
  const wasOpenRef = useRef(panelOpen);
  useEffect(() => {
    if (panelOpen && !wasOpenRef.current && scrollRef.current) scrollRef.current.scrollTop = 0;
    wasOpenRef.current = panelOpen;
  }, [panelOpen]);
  const sectionEmpty = currentSectionEmpty(items);
  // Deliberately NOT useMemo: the React Compiler refuses to compile this component when it
  // cannot preserve a manual memo, and it cannot preserve this one (a loop calling a
  // module-level helper). The scan is a single pass over the visible chat items and runs
  // only on render, so memoizing it buys nothing worth losing compilation over.
  let newestSummaryKey = null;
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i]?.metadata?.reportLink) {
      newestSummaryKey = itemKey(items[i]);
      break;
    }
  }
  // Both decisions — did content ARRIVE, and where does the view go — live in
  // `lib/chatScroll.js`, with the reasoning. `seededRef` is the state
  // `classifyChatChange` threads: it marks the first observation of a non-empty
  // thread, which is not new content arriving.
  const seededRef = useRef(false);
  useEffect(() => {
    const el = scrollRef.current;
    const newestKey = items.length ? itemKey(items[items.length - 1]) : null;
    const { isNewMessage, isNewSummary, seeded } = classifyChatChange({
      seeded: seededRef.current,
      newestKey,
      prevNewestKey: prevNewestKeyRef.current,
      newestSummaryKey,
      prevSummaryKey: prevSummaryKeyRef.current,
    });
    seededRef.current = seeded;
    prevNewestKeyRef.current = newestKey;
    // Both refs are captured ONCE per pass and the decision is made from those
    // captures, so a node can never be checked and then re-read at the call site.
    // apply() runs twice — now, and again after layout — and each pass reads the
    // refs afresh, which is what makes a node that disappeared between them a
    // no-op rather than a throw.
    const apply = () => {
      if (!el) return;
      const msg = latestMsgRef.current;
      const summaryEl = summaryMsgRef.current;
      const where = decideChatScroll({
        scrollTop: el.scrollTop,
        isNewMessage,
        isNewSummary: isNewSummary && !!summaryEl,
        newMessageOverflows: !!msg && msg.offsetHeight > el.clientHeight,
      });
      if (where === "stay") return;
      if (where === "summaryTop") summaryEl?.scrollIntoView({ block: "start" });
      else if (where === "messageTop") msg?.scrollIntoView({ block: "start" });
      else scrollToBottom();
    };
    apply();
    const id = requestAnimationFrame(apply);
    // Recorded after the rAF is scheduled, not inside apply(): apply runs twice (now and on
    // the next frame, once layout has the real height) and both runs must agree that this
    // summary is new, or the second would fall through to the bottom-scroll and undo it.
    prevSummaryKeyRef.current = newestSummaryKey;
    return () => cancelAnimationFrame(id);
  }, [
    items,
    newestSummaryKey,
    chatThinking,
    tourActive,
    tourWaitKey,
    tourExamples,
    endTourBtn,
    loading,
    activeTrace,
    scrollToBottom,
  ]);
  const SpeechRecognition =
    typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const voiceConfirmRef = useRef(null);
  const toggleListen = () => {
    if (listening) {
      beginVoiceInput(false);
      recognitionRef.current?.stop();
      setListening(false);
      if (voiceConfirmRef.current) clearTimeout(voiceConfirmRef.current);
      return;
    }
    if (!SpeechRecognition) return;
    // Settled by the first recognizer: started (ok), refused (a denied microphone), or stopped
    // first. Later recognizers of the same dictation are part of it, not new starts.
    const startIntent = beginVoiceInput(true);
    const startQuestionListen = (replaceMode, intent = null) => {
      const rec = new SpeechRecognition();
      rec.continuous = false;
      rec.interimResults = true;
      rec.lang = "en-US";
      let baseText = "";
      let baseSet = false;
      rec.onresult = (e) => {
        let final = "",
          interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) final += e.results[i][0].transcript;
          else interim += e.results[i][0].transcript;
        }
        if (!baseSet) {
          baseSet = true;
          if (!replaceMode) {
            const el = document.querySelector("textarea[placeholder]");
            baseText = el ? el.value : "";
          } else baseText = "";
        }
        if (interim) {
          setInput(baseText ? baseText + " " + interim : interim);
        }
        if (final) {
          const finalText = baseText ? baseText + " " + final : final;
          setInput(finalText);
          voiceConfirmRef.current = setTimeout(() => {
            const utt = new SpeechSynthesisUtterance("Would you like to submit this forecast?");
            utt.rate = 1.1;
            utt.onend = () => {
              const confRec = new SpeechRecognition();
              confRec.continuous = false;
              confRec.interimResults = false;
              confRec.lang = "en-US";
              confRec.onresult = (ce) => {
                const answer = (ce.results[0]?.[0]?.transcript || "").toLowerCase().trim();
                if (
                  answer.includes("yes") ||
                  answer.includes("yeah") ||
                  answer.includes("yep") ||
                  answer.includes("sure") ||
                  answer.includes("submit") ||
                  answer.includes("do it")
                ) {
                  setListening(false);
                  onSubmit();
                } else {
                  setInput("");
                  const readyUtt = new SpeechSynthesisUtterance(
                    "OK, go ahead with your new question.",
                  );
                  readyUtt.rate = 1.1;
                  readyUtt.onend = () => startQuestionListen(true);
                  window.speechSynthesis.speak(readyUtt);
                }
              };
              confRec.onend = () => {};
              confRec.onerror = () => setListening(false);
              recognitionRef.current = confRec;
              confRec.start();
            };
            window.speechSynthesis.speak(utt);
          }, 1500);
        }
      };
      rec.onend = () => {
        /* don't clear listening — confirm flow continues */
      };
      rec.onstart = () => intent?.end("ok");
      rec.onerror = (e) => {
        intent?.end(SPEECH_CANCELS.has(e?.error) ? "cancelled" : "error");
        setListening(false);
      };
      recognitionRef.current = rec;
      try {
        rec.start();
      } catch {
        intent?.end("error");
        setListening(false);
        return;
      }
      setListening(true);
    };
    startQuestionListen(false, startIntent);
  };
  const speakText = (text, idx) => {
    // A stop ends at once; a start when speech starts (`ok`), fails (`error`) or is stopped
    // before it starts (`cancelled`).
    const intent = beginIntent(
      "chat.read_aloud",
      { on: !speaking },
      { origin: "chat.read-aloud", surface: "chat" },
    );
    if (speaking) {
      intent.end("ok");
      window.speechSynthesis.cancel();
      setSpeaking(false);
      setSpeakingIdx(null);
      return;
    }
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 1.0;
    utt.onend = () => {
      setSpeaking(false);
      setSpeakingIdx(null);
    };
    utt.onstart = () => intent.end("ok");
    utt.onerror = (e) => {
      intent.end(SPEECH_CANCELS.has(e?.error) ? "cancelled" : "error");
      setSpeaking(false);
      setSpeakingIdx(null);
    };
    setSpeaking(true);
    setSpeakingIdx(idx);
    try {
      window.speechSynthesis.speak(utt);
    } catch {
      intent.end("error");
      setSpeaking(false);
      setSpeakingIdx(null);
    }
  };
  const [exampleQs, setExampleQs] = useState(() => {
    const shuffled = [...EXAMPLE_QS].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
  });
  const reshuffleExamples = () => {
    const shuffled = [...EXAMPLE_QS].sort(() => Math.random() - 0.5);
    setExampleQs(shuffled.slice(0, 3));
  };
  // Only the latest guide entry stays green and animates word-by-word; earlier ones fade
  // to white so the chat isn't a green wall of text.
  const lastGuideItem = items.reduce(
    (acc, it) => (it.kind === "entry" && it.entryKind === "guide" && it.role !== "user" ? it : acc),
    null,
  );
  const lastGuideKey = lastGuideItem ? itemKey(lastGuideItem) : null;
  // The welcome lightbulb pulses only while the welcome is the sole guide entry. Clicking
  // Continue posts the next step's narration, which ends the pulse.
  const firstGuideItem = items.find(
    (it) => it.kind === "entry" && it.entryKind === "guide" && it.role !== "user",
  );
  const firstGuideKey = firstGuideItem ? itemKey(firstGuideItem) : null;
  // The pill reads the same `forecastEngine`/`orchestrators` state as Settings, so the two
  // stay in sync automatically.
  const tierPills = TIER_ORDER.filter((t) => {
    const o = tagOwner(t);
    return o && orchestrators.includes(o);
  });
  const curTier = effectiveTier(forecastEngine);
  // While the selector is on, the pill is suppressed whatever quickDeepEnabled says, so
  // exactly one tier control is ever on screen. With no tier engines in the live roster it
  // falls back to the pre-redesign CLEAR/ENTER pair.
  const tierSelectorOn = !!quickDeepSelectorEnabled && tierPills.length > 0;
  // "New question" and Send are built once, so the click handlers (voice-stop, tour-skip,
  // reset) live in one place.
  const clearButton = (
    <IconButton
      {...ui("chat.clear")}
      // During the tour this button skips the tour instead; the later spread wins.
      {...(tourActive ? ui("tour.skip") : {})}
      label={tourActive ? "Skip tour" : "New question"}
      onClick={() => {
        if (listening) {
          recognitionRef.current?.stop();
          setListening(false);
        }
        if (tourActive) {
          onSkipTour?.();
          return;
        }
        reshuffleExamples();
        if (onReset) onReset();
      }}
    >
      <IconClose />
    </IconButton>
  );
  const submitButton = (
    <Button
      {...ui("chat.send")}
      variant="primary"
      size="sm"
      title="Send (Enter)"
      iconRight={<IconArrowUp />}
      onClick={() => {
        if (listening) {
          recognitionRef.current?.stop();
          setListening(false);
        }
        onSubmit();
      }}
      style={
        enterFlash ? { transform: "scale(0.96)", transition: "transform 0.12s ease" } : undefined
      }
    >
      Send
    </Button>
  );
  const [composerFocused, setComposerFocused] = useState(false);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>
        {".chat-divider__open:hover{color:var(--c-white)!important;text-decoration-color:currentColor!important}" +
          // A divider's time shows only after the line is hovered for a quarter second (or at once
          // while its link has keyboard focus), and hides when the pointer leaves. It keeps its
          // space, so the title never shifts.
          ".chat-divider__time{opacity:0;transition:opacity .12s}" +
          ".chat-divider:hover .chat-divider__time{opacity:1;transition-delay:.25s}" +
          ".chat-divider:focus-within .chat-divider__time{opacity:1}"}
      </style>
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 10,
          display: "flex",
          flexDirection: "column-reverse",
          gap: 8,
        }}
      >
        <div ref={endRef} style={{ height: 0 }} />
        {tourExamples && (
          <div style={{ padding: "4px 2px 2px" }}>
            <div
              style={{
                color: C.grayL,
                ...BODY,
                marginBottom: 12,
              }}
            >
              <div style={{ marginBottom: 4 }}>
                <LightbulbIcon size={11} color={C.gold} />
              </div>
              <div>
                {/* Copy says "question", not "binary question", since some examples are
                    multi-outcome; naming one question type implied the others were not allowed. */}
                <strong>Click</strong> an example, or <strong>type</strong> your own question:
              </div>
            </div>
            {/* Same type, line height and spacing as the Example Questions list below. */}
            <div style={{ display: "grid", gap: 6, ...BODY }}>
              {exampleQs.map((qq, i) => (
                <button
                  {...ui("chat.example-question")}
                  type="button"
                  key={i}
                  className="sy-btn sy-btn--ghost"
                  onClick={() => onSubmit(qq)}
                  style={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    whiteSpace: "normal",
                    lineHeight: 1.45,
                    padding: "6px 8px",
                    margin: "0 -8px",
                  }}
                >
                  {qq}
                </button>
              ))}
            </div>
          </div>
        )}
        {chatThinking && !loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 4px",
              color: C.gray,
              fontSize: 13,
            }}
          >
            <Spinner label="Thinking" />
            Thinking…
          </div>
        )}
        {[...items].reverse().map((it, ri) => {
          const key = itemKey(it);
          // ri === 0 is the newest item (reversed order); its ref lets the scroll effect pin
          // its top. Both refs are assigned since one item can be both the newest and the
          // newest summary.
          const isNewestSummary = !!newestSummaryKey && key === newestSummaryKey;
          const latestRef =
            ri === 0 || isNewestSummary
              ? (el) => {
                  if (ri === 0) latestMsgRef.current = el;
                  if (isNewestSummary) summaryMsgRef.current = el;
                }
              : null;
          if (it.kind === "divider") {
            // A history divider: the question the lines below it were about, and when.
            const canOpen = !!it.forecastClientId && !it.current && !!onOpenForecast;
            const titleStyle = {
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: it.current ? C.grayL : C.gray,
            };
            return (
              <div
                key={key}
                ref={latestRef}
                className="chat-divider"
                data-chat-divider={it.current ? "current" : ""}
                style={{
                  alignSelf: "stretch",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  margin: "8px 0 4px",
                  fontSize: 12,
                  lineHeight: "16px",
                  color: C.gray,
                }}
              >
                {canOpen ? (
                  <button
                    {...ui("chat.history-divider", it.forecastClientId)}
                    type="button"
                    className="chat-divider__open"
                    // Its name is the question: never recorded by click capture.
                    data-telemetry-redact=""
                    title={`Open: ${it.title}`}
                    onClick={() => onOpenForecast(it.forecastClientId)}
                    style={{
                      ...titleStyle,
                      background: "none",
                      border: "none",
                      padding: 0,
                      font: "inherit",
                      cursor: "pointer",
                      textAlign: "left",
                      textDecoration: "underline",
                      textDecorationColor: C.border,
                      textUnderlineOffset: 3,
                    }}
                  >
                    {it.title}
                  </button>
                ) : (
                  <span title={it.title} style={titleStyle}>
                    {it.title}
                  </span>
                )}
                <span aria-hidden="true" style={{ flex: 1, minWidth: 8 }} />
                <span className="sy-num chat-divider__time" style={{ flex: "none" }}>
                  {formatDividerTime(it.at)}
                </span>
              </div>
            );
          }
          if (it.kind === "entry") {
            const isUser = it.role === "user";
            const isGuide = it.entryKind === "guide" && !isUser;
            const isLegend = it.entryKind === "legend" || !!it.metadata?.legend;
            const isExamples = it.entryKind === "examples";
            // The Full Report link opens the same report modal as the pie click.
            const isReportLink = !!it.metadata?.reportLink;
            const isLatestTour =
              isGuide && key === lastGuideKey && (tourActive || postTourActive) && !tourExamples;
            const isWelcomeGuide =
              isGuide && key === firstGuideKey && firstGuideKey === lastGuideKey && tourActive;
            // The user's own turn sits on the right, marked with a person; Sayer's on the left.
            if (isUser && !isLegend && !isExamples && !isReportLink) {
              return (
                <div
                  className="chat-message chat-message--user"
                  key={key}
                  ref={latestRef}
                  style={{
                    alignSelf: "flex-end",
                    maxWidth: "85%",
                    display: "flex",
                    flexDirection: "row-reverse",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <span
                    role="img"
                    aria-label="You"
                    style={{ display: "flex", flex: "none", marginTop: 7, color: C.gray }}
                  >
                    <IconAccount size={14} />
                  </span>
                  <div
                    className="chat-message__body"
                    style={{
                      ...BODY,
                      textAlign: "left",
                      background: C.rowInactive,
                      padding: "6px 10px",
                    }}
                  >
                    <Linkify text={it.content} />
                  </div>
                </div>
              );
            }
            return (
              <div
                className="chat-message"
                key={key}
                ref={latestRef}
                style={{ width: "100%", alignSelf: "stretch" }}
              >
                {/* A transcript: every turn opens with its speaker, none in a bubble. */}
                <div className="chat-message__header" style={{ justifyContent: "flex-start" }}>
                  <div className="chat-message__mark" style={{ gap: 6, marginBottom: 4 }}>
                    <span
                      className="sy-kicker"
                      style={{
                        color: isUser ? C.gray : isGuide ? C.gold : C.accent,
                        ...(isWelcomeGuide
                          ? { animation: "pulse-text 2s ease-in-out infinite" }
                          : {}),
                      }}
                    >
                      {isUser ? "You" : isGuide ? "Tour" : "Sayer"}
                    </span>
                  </div>
                  {!isUser && voiceMode && (
                    <ReadAloudButton
                      text={it.content}
                      itemKey={key}
                      speakingIdx={speakingIdx}
                      onSpeak={speakText}
                    />
                  )}
                </div>
                <div
                  className="chat-message__body"
                  style={{
                    ...BODY,
                    textAlign: "left",
                    transition: "color 1s ease",
                    ...(isGuide ? { color: C.grayL } : {}),
                  }}
                >
                  {isGuide ? (
                    <TourText text={it.content} animate={isLatestTour} onReveal={scrollToBottom} />
                  ) : isReportLink ? (
                    // Only the REPORT is clamped. An ordinary chat line is short by nature,
                    // and clamping it would cut a reply that has nowhere else to be read.
                    <ClampedReportBody consoleRef={scrollRef}>
                      <Linkify text={it.content} />
                    </ClampedReportBody>
                  ) : (
                    <Linkify text={it.content} />
                  )}
                  {isLegend && (
                    <div
                      style={{
                        marginTop: 6,
                        fontStyle: "normal",
                        fontSize: 12,
                        lineHeight: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 22,
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          animation: "chatSlideUp 0.45s ease-out both",
                          animationDelay: "0.45s",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: 5,
                          }}
                        >
                          <PieIcon p={0.62} sz={24} color={C.gray} bg={C.future} />
                        </div>
                        <div className="chat-legend-label">Slice = Probability</div>
                        <div>The chance the trigger occurs.</div>
                      </div>
                      <div
                        style={{
                          animation: "chatSlideUp 0.45s ease-out both",
                          animationDelay: "0.8s",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "center",
                            gap: 6,
                            marginBottom: 5,
                          }}
                        >
                          <PieIcon p={0.6} sz={14} color={C.gray} bg={C.future} />
                          <PieIcon p={0.6} sz={24} color={C.gray} bg={C.future} />
                        </div>
                        <div className="chat-legend-label">Size = Impact</div>
                        <div>How much the forecast would change if the trigger occurs.</div>
                      </div>
                    </div>
                  )}
                  {isExamples && (
                    <div style={{ marginTop: 2 }}>
                      <div
                        style={{
                          color: C.gray,
                          fontSize: 12,
                          marginBottom: 6,
                          fontStyle: "normal",
                        }}
                      >
                        Example questions
                      </div>
                      {/* Styled to MATCH the live footer list above (the `tourExamples`
                          block): C.gray, 1px tracking, 8px gap, and the same clickable button,
                          with .chat-example-q pinning Roboto on both. C.gray is a per-theme
                          token, so it suits light themes too. */}
                      {exampleQs.map((qq, qi) => (
                        <button
                          {...ui("chat.example-question")}
                          type="button"
                          key={qi}
                          className="chat-example-q"
                          onClick={() => onSubmit(qq)}
                          style={{
                            cursor: "pointer",
                            color: C.gray,
                            // Within-question line spacing and the Roboto face come from
                            // .chat-example-q; the gap BETWEEN questions stays here, and must
                            // remain clearly larger than the line spacing or the wrapped lines
                            // of one question read as separate questions.
                            fontSize: 12,
                            letterSpacing: "0px",
                            marginBottom: qi === exampleQs.length - 1 ? 0 : 8,
                            fontStyle: "normal",
                            background: "none",
                            border: "none",
                            padding: 0,
                            fontFamily: "inherit",
                            textAlign: "inherit",
                            display: "block",
                            width: "100%",
                          }}
                        >
                          {qq}
                        </button>
                      ))}
                    </div>
                  )}
                  {isReportLink && onOpenReport && (
                    <div style={{ marginTop: 8 }}>
                      {/* A summary from another forecast in the history opens that forecast;
                          the report button would open the one on screen. */}
                      {it._foreign && onOpenForecast ? (
                        <Button
                          {...ui("chat.open-question", it._forecastClientId)}
                          size="sm"
                          onClick={() => onOpenForecast(it._forecastClientId)}
                          iconRight={<IconArrowRight />}
                        >
                          Open this question
                        </Button>
                      ) : (
                        <Button
                          {...ui("chat.report-link")}
                          size="sm"
                          onClick={onOpenReport}
                          iconRight={<IconArrowRight />}
                        >
                          Open full report
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          }
          if (it.kind === "narration") {
            // Model-written text is passed straight through; the only string of ours is the
            // optional milestone label.
            return (
              <div
                className="chat-message"
                key={key}
                ref={latestRef}
                data-testid="chat-narration"
                style={{ maxWidth: "85%", alignSelf: "flex-start" }}
              >
                <div
                  className="chat-message__body"
                  style={{ ...BODY, fontSize: 12, color: C.gray, textAlign: "left" }}
                >
                  {it.phase && <span style={{ opacity: 0.8, marginRight: 6 }}>{it.phase} ·</span>}
                  <Linkify text={it.content} />
                </div>
              </div>
            );
          }
          const isPending = !it.terminal;
          if (it.status === "errored") {
            // Distinct chrome keeps an errored run from reading as an ordinary Sayer
            // reply; the visible "Error" label, not the red tint, is the non-colour signal.
            // Only the newest line is a live region, so loading a history of errors
            // doesn't make a screen reader announce every one.
            return (
              <div
                className="chat-message"
                key={key}
                ref={latestRef}
                style={{ maxWidth: "85%", alignSelf: "flex-start" }}
              >
                <div
                  className="chat-error"
                  role={ri === 0 ? "alert" : undefined}
                  data-testid="chat-error"
                  data-error-detail={it.detail || undefined}
                >
                  <div
                    className="chat-error__label"
                    style={{
                      fontFamily: "inherit",
                      textTransform: "none",
                      letterSpacing: 0,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <AlertIcon size={12} />
                    <span>Something went wrong</span>
                    {voiceMode && (
                      <ReadAloudButton
                        text={it.content || ""}
                        itemKey={key}
                        speakingIdx={speakingIdx}
                        onSpeak={speakText}
                      />
                    )}
                  </div>
                  <div className="chat-message__body chat-error__body" style={BODY}>
                    <Linkify text={it.content || ""} />
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div
              className="chat-message"
              key={key}
              ref={latestRef}
              style={{ width: "100%", alignSelf: "stretch" }}
            >
              <div className="chat-message__header" style={{ justifyContent: "flex-start" }}>
                <div className="chat-message__mark" style={{ gap: 6, marginBottom: 4 }}>
                  <span className="sy-kicker" style={{ color: C.accent }}>
                    Sayer
                  </span>
                </div>
                {voiceMode && !isPending && (
                  <ReadAloudButton
                    text={it.content || ""}
                    itemKey={key}
                    speakingIdx={speakingIdx}
                    onSpeak={speakText}
                  />
                )}
              </div>
              <div
                className="chat-message__body"
                data-telemetry-ticking={isPending ? "" : undefined}
                style={{ ...BODY, textAlign: "left" }}
              >
                <Linkify text={it.content || ""} />
                {isPending && (
                  <span className="dots-anim">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {/* Shown when the current section is empty, which with history above means right under
            the newest divider: order -1 puts it at the bottom of the column-reverse list. */}
        {sectionEmpty && !tourExamples && (
          <div style={{ order: -1 }}>
            {readOnly ? (
              // Recipients get no owner-only hints or examples, since they cannot edit.
              <div style={{ ...BODY, color: C.gray, padding: "0 4px 4px" }}>
                <div style={{ color: C.grayL, fontWeight: 500, marginBottom: 4 }}>
                  Shared with you
                </div>
                Ask anything about this forecast. Your questions here are your own; the owner does
                not see them.
              </div>
            ) : askHeroShowing ? (
              // The examples live on the canvas while the Ask hero shows.
              <div style={{ ...BODY, color: C.gray, padding: "0 4px 4px" }}>
                Ask a question here or in the main panel, or ask Sayer how forecasts work.
              </div>
            ) : (
              <div style={{ ...BODY, color: C.gray, padding: "0 4px 4px" }}>
                <div style={{ color: C.grayL, fontWeight: 500, marginBottom: 8 }}>
                  {forecast ? "Ask about this forecast" : "Example questions"}
                </div>
                {forecast ? (
                  <div style={{ display: "grid", gap: 4 }}>
                    <span>Why is the chance where it is?</span>
                    <span>What if [a trigger] happens?</span>
                    <span>What sources back this forecast?</span>
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 6 }}>
                    {exampleQs.map((q, i) => (
                      <button
                        {...ui("chat.example-fill")}
                        type="button"
                        key={i}
                        className="sy-btn sy-btn--ghost"
                        onClick={() => setInput(q)}
                        style={{
                          justifyContent: "flex-start",
                          textAlign: "left",
                          whiteSpace: "normal",
                          lineHeight: 1.45,
                          padding: "6px 8px",
                          margin: "0 -8px",
                        }}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ padding: "8px 12px 12px", flexShrink: 0 }}>
        {tourActive && (
          <div
            style={{
              paddingBottom: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            {tourWaitKey === "next" || tourWaitKey === "continue" ? (
              <Button variant="primary" size="sm" onClick={onTourNext} {...ui("tour.next")}>
                {tourWaitKey === "continue" ? "Continue" : "Next"}
              </Button>
            ) : (
              <span />
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onSkipTour}
              title="End the tour"
              {...ui("tour.skip")}
            >
              Skip tour
            </Button>
          </div>
        )}
        {!tourActive && postTourActive && !endTourBtn && (
          <div style={{ paddingBottom: 8, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={onEndPostTour}
              title="End the tour"
              {...ui("tour.skip")}
            >
              Skip tour
            </Button>
          </div>
        )}
        {endTourBtn && (
          <div style={{ paddingBottom: 8 }}>
            <Button variant="primary" size="sm" onClick={onEndTourBtn} {...ui("tour.end")}>
              End tour
            </Button>
          </div>
        )}
        {!forecastEngine ? (
          <div style={{ padding: "16px 8px", textAlign: "center", ...BODY, color: C.gray }}>
            {healthError ? (
              // Only a first-time visitor reaches here (a returning user has a persisted
              // engine), so this is their whole composer until discovery recovers.
              <div
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}
              >
                <div>Couldn't reach Sayer. It may be starting up — this can take a moment.</div>
                <Button {...ui("chat.retry-connection")} size="sm" onClick={onRetryHealth}>
                  Try again
                </Button>
              </div>
            ) : (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Spinner label="Connecting" />
                Connecting to Sayer…
              </div>
            )}
          </div>
        ) : (
          <>
            {canCancel && onCancelForecast && !runStatusShowing ? (
              <div style={{ paddingBottom: 8 }}>
                <CancelForecastControl canCancel onCancel={onCancelForecast} compact />
              </div>
            ) : null}
            {quickDeepEnabled && !tierSelectorOn && tierPills.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  marginBottom: 6,
                  userSelect: "none",
                  color: C.gray,
                }}
              >
                Depth:
                {tierPills.map((t) => (
                  <button
                    {...ui("chat.tier-option", t)}
                    key={t}
                    type="button"
                    aria-pressed={curTier === t}
                    onClick={() => onSelectTier && onSelectTier(t)}
                    title={`Switch to ${TIER_SHORT[t]}`}
                    className="sy-token"
                    style={curTier === t ? { borderColor: C.accent, color: C.white } : undefined}
                  >
                    {TIER_SHORT[t]}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
              <div
                ref={composerBoxRef}
                style={{
                  // No `overflow: hidden`: the depth menu overlays this box.
                  flex: 1,
                  minWidth: 0,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  background: C.inputBg,
                  border: `1px solid ${
                    listening ? C.danger : composerFocused ? C.accent : C.border
                  }`,
                  borderRadius: 12,
                  transition: "border-color 0.15s",
                }}
              >
                <textarea
                  {...ui("chat.input")}
                  data-chat-input
                  className={`${proColors ? "pro-input " : ""}slow-caret`}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onFocus={() => setComposerFocused(true)}
                  onBlur={() => setComposerFocused(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (listening) {
                        recognitionRef.current?.stop();
                        setListening(false);
                      }
                      onSubmit();
                    }
                  }}
                  placeholder={
                    forecast ? "Ask about this forecast…" : "Ask a question about the future…"
                  }
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    borderRadius: 12,
                    color: C.white,
                    font: "inherit",
                    ...BODY,
                    padding: "10px 12px 4px",
                    resize: "none",
                    height: 76,
                    outline: "none",
                    boxSizing: "border-box",
                    display: "block",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "4px 6px 6px 4px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, display: "flex" }}>
                    {tierSelectorOn && (
                      <TierSelector
                        tiers={tierPills}
                        current={curTier}
                        onSelect={onSelectTier}
                        C={C}
                        align="start"
                      />
                    )}
                  </div>
                  {/* "New question" lives in the rail; here the button is only the tour's skip. */}
                  {tourActive && clearButton}
                  {submitButton}
                </div>
              </div>
              {voiceMode && SpeechRecognition && (
                <IconButton
                  {...ui("chat.voice-input")}
                  label={listening ? "Stop listening" : "Start voice input"}
                  aria-pressed={listening}
                  onClick={toggleListen}
                  style={{
                    height: "auto",
                    width: 36,
                    flexShrink: 0,
                    border: `1px solid ${listening ? C.danger : C.border}`,
                    color: listening ? C.danger : undefined,
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="9" y="2" width="6" height="11" rx="3" />
                    <path d="M19 10v1a7 7 0 0 1-14 0v-1M12 18v4M8 22h8" />
                  </svg>
                </IconButton>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
